// Firebase Storage utilities for image persistence

import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

/**
 * Upload a blob to Firebase Storage and return the download URL
 * @param {Blob} blob - The image blob to upload
 * @param {string} userId - User ID for organizing files
 * @param {string} filename - Optional custom filename
 * @returns {Promise<string>} Download URL
 */
export async function uploadImageToStorage(blob, userId, filename = null) {
  try {
    // Create a unique filename if not provided
    const finalFilename = filename || `mockup_${Date.now()}.png`;
    
    // Create storage reference
    const storageRef = ref(storage, `users/${userId}/mockups/${finalFilename}`);
    
    // Upload the blob
    const snapshot = await uploadBytes(storageRef, blob);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image to storage:', error);
    throw error;
  }
}

/**
 * Delete an image from Firebase Storage
 * @param {string} downloadURL - The download URL of the image to delete
 */
export async function deleteImageFromStorage(downloadURL) {
  try {
    // Extract the path from the download URL
    const url = new URL(downloadURL);
    const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
    
    const imageRef = ref(storage, path);
    await deleteObject(imageRef);
  } catch (error) {
    console.error('Error deleting image from storage:', error);
    throw error;
  }
}

/**
 * Convert blob to base64 string for Firestore storage
 * @param {Blob} blob - The blob to convert
 * @returns {Promise<string>} Base64 data URL
 */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Check if an image should use Firebase Storage vs base64
 * @param {Blob} blob - The image blob
 * @returns {boolean} True if should use storage, false for base64
 */
export function shouldUseStorage(blob) {
  // Use storage for images larger than 500KB
  const sizeThreshold = 500 * 1024; // 500KB
  return blob.size > sizeThreshold;
}
